import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Primitives";
import { TrustStrip } from "@/components/site/Sections";
import { ContactStep, OTHER, TripStep } from "@/components/booking/Steps";
import { ReviewStep, SummaryCard } from "@/components/booking/Review";
import { api, formatApiError } from "@/lib/api";
import { AIRPORT, locationsFrom, matchPrice, useRoutes } from "@/lib/useRoutes";

const INITIAL = {
  pickup: AIRPORT, pickup_other: "", pickup_details: "", dropoff: "", dropoff_other: "", dropoff_details: "",
  date: "", time: "", flight_number: "", passengers: 2, luggage: 2, child_seats: false, child_seat_count: 1,
  meet_greet: false, name_sign: "", name: "", email: "", phone_code: "+44", phone: "", notes: "",
  payment_method: "driver", accept_terms: false, website: "",
};
const STEP_LABELS = ["Trip details", "Contact details", "Summary & payment"];
const resolve = (f, k) => (f[k] === OTHER ? f[`${k}_other`].trim() : f[k]);

function validate(step, f) {
  const e = {};
  if (step === 0) {
    if (!f.pickup) e.pickup = "Please choose a pickup location.";
    if (f.pickup === OTHER && f.pickup_other.trim().length < 2) e.pickup_other = "Please enter the pickup location.";
    if (!f.dropoff) e.dropoff = "Please choose a drop-off location.";
    if (f.dropoff === OTHER && f.dropoff_other.trim().length < 2) e.dropoff_other = "Please enter the drop-off location.";
    if (resolve(f, "pickup") && resolve(f, "pickup").toLowerCase() === resolve(f, "dropoff").toLowerCase()) e.dropoff = "Drop-off must differ from pickup.";
    if (!f.date) e.date = "Please choose a date.";
    else if (f.date < new Date().toISOString().slice(0, 10)) e.date = "The date cannot be in the past.";
    if (!f.time) e.time = "Please choose a time.";
  }
  if (step === 1) {
    if (f.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Please enter a valid email address.";
    if (!/^[\d\s\-()]{5,20}$/.test(f.phone)) e.phone = "Please enter a valid phone number.";
  }
  if (step === 2 && !f.accept_terms) e.accept_terms = "Please accept the terms to continue.";
  return e;
}

const Stepper = ({ step }) => (
  <ol className="flex gap-2" data-testid="booking-stepper">
    {STEP_LABELS.map((l, i) => (
      <li key={l} className="flex-1">
        <div className={`h-[3px] rounded-full transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-slate-200"}`} />
        <p className={`mt-3 text-[11px] uppercase tracking-[0.18em] ${i === step ? "text-ink" : "text-slate-400"}`}>
          <span className="hidden sm:inline">{`0${i + 1} · `}</span>{l}
        </p>
      </li>
    ))}
  </ol>
);

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: routes = [] } = useRoutes();
  const [f, setF] = useState(INITIAL);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const locations = useMemo(() => locationsFrom(routes), [routes]);

  useEffect(() => {
    const p = params.get("pickup");
    const d = params.get("dropoff");
    if (p || d) setF((x) => ({ ...x, pickup: p || x.pickup, dropoff: d || x.dropoff }));
  }, [params]);

  const pickup = resolve(f, "pickup");
  const dropoff = resolve(f, "dropoff");
  const price = matchPrice(routes, pickup, dropoff);

  useEffect(() => {
    if (price == null && f.payment_method === "online") setF((x) => ({ ...x, payment_method: "driver" }));
  }, [price, f.payment_method]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const next = () => {
    const e = validate(step, f);
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please check the highlighted fields.");
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    const e = validate(2, f);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    const { pickup_other, dropoff_other, accept_terms, ...rest } = f;
    try {
      const { data } = await api.post("/bookings", {
        ...rest, pickup, dropoff, flight_number: f.flight_number.toUpperCase(), origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else navigate(`/booking/confirmation/${data.booking_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Book Your Rhodes Transfer Online" description="Book a private Rhodes airport transfer in under 2 minutes. Fixed prices per vehicle, pay online or pay the driver. Flight tracking included." path="/booking" />
      <section className="container-x pb-24 pt-28 md:pt-36">
        <Reveal>
          <span className="eyebrow">Booking</span>
          <h1 className="heading-xl mt-6 text-ink">Book your private transfer</h1>
          <div className="mt-6"><TrustStrip /></div>
        </Reveal>
        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Stepper step={step} />
            <div className="card-soft mt-8 border border-slate-200 bg-white p-6 md:p-10">
              <input className="hp-field" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} aria-hidden="true" name="website" />
              {step === 0 && <TripStep f={f} set={set} errors={errors} locations={locations} />}
              {step === 1 && <ContactStep f={f} set={set} errors={errors} />}
              {step === 2 && <ReviewStep f={f} set={set} price={price} errors={errors} />}
              <div className="mt-10 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
                {step > 0 ? (
                  <button type="button" onClick={() => setStep((s) => s - 1)} className="btn btn-outline" data-testid="booking-back-button">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                ) : <span />}
                {step < 2 ? (
                  <button type="button" onClick={next} className="btn btn-dark" data-testid="booking-next-button">
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={submitting} className="btn btn-gold" data-testid="booking-submit-button">
                    {submitting ? "Processing…" : f.payment_method === "online" ? "Continue to secure payment" : "Confirm booking"}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <SummaryCard pickup={pickup} dropoff={dropoff} f={f} price={price} />
          </div>
        </div>
      </section>
    </>
  );
}
